import { resolveLaneByLocations as resolveLaneByLocationsOp } from '@gql/operations'
import { getLogger, type GqlExecutor } from '@hopdrive/sdk-core'
import type { ResolveLaneByLocationsQuery } from '@gql/schema'
import type { Lane, ResolveLaneInput, ResolveLaneResult } from '../../types'
import { createLaneShell } from './createLaneShell'
import { generateOperationId, scopeLogger, elapsed } from '../../logging'

type LaneWithInverseResult = ResolveLaneByLocationsQuery['lanes'][number]

export interface ResolveLaneParams extends ResolveLaneInput {
  executor?: GqlExecutor
  /** Optional correlation ID for log tracing across services */
  correlationId?: string
}

/**
 * Find existing lane or create new shell pair.
 */
export async function resolveLane(params: ResolveLaneParams): Promise<ResolveLaneResult> {
  const log = scopeLogger(getLogger(), {
    operationId: generateOperationId(),
    operation: 'resolveLane',
    customerId: params.customerId,
    ...(params.correlationId && { correlationId: params.correlationId }),
  })
  const startedAt = Date.now()

  log.debug('resolveLane.start', {
    customerId: params.customerId,
    originLocationId: params.originLocationId,
    destinationLocationId: params.destinationLocationId,
  })

  // Try to find existing lane
  const lanes = await resolveLaneByLocationsOp(
    {
      customerId: params.customerId,
      originLocationId: params.originLocationId,
      destinationLocationId: params.destinationLocationId,
    },
    { executor: params.executor }
  )

  const existingLane = lanes?.[0] as LaneWithInverseResult | undefined

  if (existingLane) {
    log.info('resolveLane.found', { laneId: existingLane.id, elapsedMs: elapsed(startedAt) })
    return {
      lane: existingLane as Lane,
      inverse: existingLane.inverse as Lane | null,
      created: false,
    }
  }

  // Create new lane shell pair
  const lanePair = await createLaneShell({
    customerId: params.customerId,
    originLocationId: params.originLocationId,
    destinationLocationId: params.destinationLocationId,
    executor: params.executor,
  })

  log.info('resolveLane.created', {
    forwardId: lanePair.forward.id,
    inverseId: lanePair.inverse?.id,
    elapsedMs: elapsed(startedAt),
  })

  return {
    lane: lanePair.forward,
    inverse: lanePair.inverse,
    created: true,
  }
}
